import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { useForm, Controller } from 'react-hook-form';
import {
  useGetAccountBalancesByCategory,
  useUpdateAccount,
  useCreateAccount,
  useDeleteAccount,
  useUpdateLiability,
  useCreateLiability,
  useDeleteLiability,
  useMutateSettlementAdjustment,
  useDeleteSettlementAdjustment
} from '../../api/accounts';
import { useGetCaseCategories, useGetDefaultAssociatedLiabilityMap } from '../../api/categories';
import {
  TextField,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Chip,
  Skeleton,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  FiberNew as FiberNewIcon,
  Edit as EditIcon,
  Paid as PaidIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import {
  ActionIconButton,
  FormTableHeaderCell,
  FormTableDetailCell,
  FormTableLiabilityCell,
  FormTableEquityCell,
  FormTableTotalCell,
  NotesField,
  ValuePercentField,
  TrackedTextField,
  TrackedAutocomplete,
  TrackedNumberField
} from './management-form-elements';
import { formatDate, formatCurrency, formatPercentage, formatOwnershipAllocation } from '../../utils/formatters';
import AccountTitleEditDialog from './AccountTitleEditDialog';
import AccountSourceEditDialog from './AccountSourceEditDialog';
import { useGetEntityNetwork } from '../../api/entities';
import { Badge } from '../../design-system';
import { getAccountStatusBadges } from '../../utils/accountStatus';

// Account Title cell — shows the entity title allocation (title[]) with an edit
// affordance (opens AccountTitleEditDialog), falling back to the legacy
// account_holder text. The raw "as scanned" OCR string is never lost — it is
// persisted on the account and surfaced on hover (see titleHint) rather than
// rendered inline, which used to consume too much vertical space in the table.
function TitleCell({ account, onEdit, readonly }) {
  const allocation = formatOwnershipAllocation(account?.title, { abbreviate: true });
  const fullAllocation = formatOwnershipAllocation(account?.title);
  const needsReview = Array.isArray(account?.title) && account.title.some((t) => t?.needs_review);
  // On-screen, so the full legal name and the raw scanned string stay one hover
  // away without taking up row height.
  const titleHint = [
    fullAllocation && fullAllocation !== allocation ? fullAllocation : null,
    account?.account_holder ? `As scanned: ${account.account_holder}` : null,
    needsReview ? 'Title needs review' : null,
  ].filter(Boolean).join(' — ') || undefined;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {allocation ? (
          <Typography variant="body2" sx={{ fontSize: 'var(--text-xs)' }} title={titleHint}>
            {allocation}{needsReview ? ' *' : ''}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: 'var(--text-xs)' }} title={account?.account_holder ? `As scanned: ${account.account_holder}` : undefined}>
            {account?.account_holder || '—'}
          </Typography>
        )}
      </Box>
      {!readonly ? (
        <IconButton size="small" aria-label="Edit title" onClick={onEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
}

// Account-ownership Step 3: stated source-of-funds cell — read-only display of a
// party's source share (% + $). Editing the whole allocation (petitioner /
// respondent / any other entity) is a role-neutral row action in the Actions column
// (the "Edit source of funds" icon opens AccountSourceEditDialog); it is deliberately
// not bound to any one party's cell, since a case may have no declared petitioner /
// respondent. Remainder = the marital presumption.
function SourceCell({ account, role }) {
  const structure = role === 'petitioner' ? account?.petitioner_owning_structure : account?.respondent_owning_structure;
  const pct = structure?.percentage || 0;
  const end = account?.latest_balance?.end_balance || 0;
  const value = structure?.value ?? pct * end;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ fontSize: 'var(--text-xs)' }}>{formatCurrency(value)}</Typography>
      <Typography variant="caption" color="text.secondary">{formatPercentage(pct)}</Typography>
    </Box>
  );
}

// Skeleton loading component for tables
const AccountTableSkeleton = () => (
  <Box sx={{ mb: 4 }}>
    <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            {[...Array(15)].map((_, i) => (
              <FormTableHeaderCell key={i}>
                <Skeleton variant="text" />
              </FormTableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(5)].map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {[...Array(15)].map((_, colIndex) => (
                <FormTableDetailCell key={colIndex}>
                  <Skeleton variant="rectangular" height={30} />
                </FormTableDetailCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <Skeleton variant="rectangular" width={150} height={36} sx={{ mt: 1 }} />
  </Box>
);

const getLocalDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to check if a record is new (not yet saved to server)
const isNewRecord = (record) => {
  return record._isNew === true;
};

const AccountManagementForm = () => {
  const { data: initialData, error: accountError, isLoading: isAccountLoading } = useGetAccountBalancesByCategory();
  // Prefetch the entity network into SWR's cache so the Title edit dialog opens
  // with the entity pick-list already warm (eliminates the first-open fetch delay).
  useGetEntityNetwork(true);
  if (initialData) {
    console.log('Initial Data:', initialData);
  }
  const { data: categoryList } = useGetCaseCategories(true, "asset-liability");
  const { data: defaultAssociatedLiabilityMap } = useGetDefaultAssociatedLiabilityMap();

  // Mutation hooks
  const updateAccount = useUpdateAccount();
  const createAccount = useCreateAccount();
  const deleteAccount = useDeleteAccount();
  const updateLiability = useUpdateLiability();
  const createLiability = useCreateLiability();
  const deleteLiability = useDeleteLiability();
  const mutateSettlementAdjustment = useMutateSettlementAdjustment();
  const deleteSettlementAdjustment = useDeleteSettlementAdjustment();

  const { mutate } = useSWRConfig();
  const subcategoryRefs = useRef({});

  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryType, setNewCategoryType] = useState('assets');
  const [notesDialog, setNotesDialog] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({
    assets: false,
    liabilities: false
  });
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Track saving state per record
  const [savingRecords, setSavingRecords] = useState(new Set());
  const [saveErrors, setSaveErrors] = useState(new Map());
  const saveTimeouts = useRef(new Map());

  const { control, handleSubmit, setValue, getValues, formState: { errors }, trigger, reset, watch } = useForm({
    defaultValues: initialData || {},
    mode: 'onBlur'
  });

  const watchedData = watch();
  // Account-ownership Step 4: which account/liability's Title is being edited
  // (entity-picker dialog). Null when closed.
  const [titleEditAccount, setTitleEditAccount] = useState(null);
  const [sourceEditAccount, setSourceEditAccount] = useState(null);

  // Reset form when initial data loads
  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  const toggleSection = (sectionType) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionType]: !prev[sectionType]
    }));
  };

  const toggleCategory = (accountType, categoryName) => {
    const key = `${accountType}-${categoryName}`;
    setCollapsedCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Helper to get record key for tracking saves
  const getRecordKey = (account_type, categoryName, accountIndex, liabilityIndex = null) => {
    // Get the actual account to use its ID for a stable key
    const account = getValues(`${account_type}.${categoryName}.accounts.${accountIndex}`);
    if (!account) {
      // Fallback to index-based key if account not found
      if (liabilityIndex !== null) {
        return `${account_type}-${categoryName}-${accountIndex}-liability-${liabilityIndex}`;
      }
      return `${account_type}-${categoryName}-${accountIndex}`;
    }

    if (liabilityIndex !== null) {
      const liability = account.associated_liabilities?.[liabilityIndex];
      if (!liability) {
        return `${account_type}-${categoryName}-${account.id}-liability-${liabilityIndex}`;
      }
      return `${account_type}-${categoryName}-${account.id}-liability-${liability.id}`;
    }
    return `${account_type}-${categoryName}-${account.id}`;
  };

  // Save individual account
  const saveAccountRecord = useCallback(async (account_type, categoryName, accountIndex) => {
    const recordKey = getRecordKey(account_type, categoryName, accountIndex);

    // Validate the record first
    const fieldsToValidate = [
      `${account_type}.${categoryName}.accounts.${accountIndex}.subcategory`,
      `${account_type}.${categoryName}.accounts.${accountIndex}._displayName`,
      `${account_type}.${categoryName}.accounts.${accountIndex}.latest_balance.end_balance`,
      `${account_type}.${categoryName}.accounts.${accountIndex}.petitioner_owning_structure`,
      `${account_type}.${categoryName}.accounts.${accountIndex}.respondent_owning_structure`,
      `${account_type}.${categoryName}.accounts.${accountIndex}.petitioner_settlement_structure`,
    ];

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) {
      setSaveErrors(prev => new Map(prev).set(recordKey, 'Validation failed'));
      return;
    }

    const account = getValues(`${account_type}.${categoryName}.accounts.${accountIndex}`);

    // Check if this is a new record
    const isNew = isNewRecord(account);

    setSavingRecords(prev => new Set(prev).add(recordKey));
    setSaveErrors(prev => {
      const next = new Map(prev);
      next.delete(recordKey);
      return next;
    });

    try {
      if (isNew) {
        // Create new account - server will use the client-generated UUID
        const accountData = {
          id: account.id, // Pass the UUID to server
          subcategory: account.subcategory,
          _displayName: account._displayName,
          account_holder: account.account_holder,
          latest_balance: {
            id: account.latest_balance.id, // Pass balance UUID too
            _source: account.latest_balance._source,
            end_date: account.latest_balance.end_date,
            end_balance: account.latest_balance.end_balance,
          },
          notes: account.notes,
          petitioner_owning_structure: account.petitioner_owning_structure,
          respondent_owning_structure: account.respondent_owning_structure,
          petitioner_settlement_structure: account.petitioner_settlement_structure,
        };

        await createAccount(account_type === 'assets' ? 'Asset' : 'Liability', categoryName, accountData);

        // Mark as no longer new
        setValue(`${account_type}.${categoryName}.accounts.${accountIndex}._isNew`, false);
      } else {
        // Update existing account
        const updates = {
          subcategory: account.subcategory,
          _displayName: account._displayName,
          account_holder: account.account_holder,
          latest_balance: {
            _source: account.latest_balance._source,
            end_date: account.latest_balance.end_date,
            end_balance: account.latest_balance.end_balance,
          },
          notes: account.notes,
          petitioner_owning_structure: account.petitioner_owning_structure,
          respondent_owning_structure: account.respondent_owning_structure,
          petitioner_settlement_structure: account.petitioner_settlement_structure,
        };

        await updateAccount(account.id, updates);
      }
    } catch (error) {
      console.error('Failed to save account:', error);
      setSaveErrors(prev => new Map(prev).set(recordKey, 'Save failed'));
    } finally {
      setSavingRecords(prev => {
        const next = new Set(prev);
        next.delete(recordKey);
        return next;
      });
    }
  }, [trigger, getValues, setValue, createAccount, updateAccount]);

  // Save individual liability
  const saveLiabilityRecord = useCallback(async (account_type, categoryName, accountIndex, liabilityIndex) => {
    const recordKey = getRecordKey(account_type, categoryName, accountIndex, liabilityIndex);

    // Validate the record first
    const fieldsToValidate = [
      `${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.category`,
      `${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.subcategory`,
      `${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}._displayName`,
      `${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.latest_balance.end_balance`,
    ];

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) {
      setSaveErrors(prev => new Map(prev).set(recordKey, 'Validation failed'));
      return;
    }

    const account = getValues(`${account_type}.${categoryName}.accounts.${accountIndex}`);
    const liability = account.associated_liabilities[liabilityIndex];

    // Check if this is a new record
    const isNew = isNewRecord(liability);

    setSavingRecords(prev => new Set(prev).add(recordKey));
    setSaveErrors(prev => {
      const next = new Map(prev);
      next.delete(recordKey);
      return next;
    });

    try {
      if (isNew) {
        // If parent account is also new, save it first
        if (isNewRecord(account)) {
          await saveAccountRecord(account_type, categoryName, accountIndex);
          // Re-fetch the account to get the updated state
          const updatedAccount = getValues(`${account_type}.${categoryName}.accounts.${accountIndex}`);

          const liabilityData = {
            id: liability.id, // Pass the UUID to server
            category: liability.category,
            subcategory: liability.subcategory,
            _displayName: liability._displayName,
            account_holder: liability.account_holder,
            latest_balance: {
              id: liability.latest_balance.id, // Pass balance UUID too
              _source: liability.latest_balance._source,
              end_date: liability.latest_balance.end_date,
              end_balance: liability.latest_balance.end_balance,
            },
            notes: liability.notes,
          };

          await createLiability(updatedAccount.id, liabilityData);

          // Mark as no longer new
          setValue(`${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}._isNew`, false);
        } else {
          const liabilityData = {
            id: liability.id, // Pass the UUID to server
            category: liability.category,
            subcategory: liability.subcategory,
            _displayName: liability._displayName,
            account_holder: liability.account_holder,
            latest_balance: {
              id: liability.latest_balance.id, // Pass balance UUID too
              _source: liability.latest_balance._source,
              end_date: liability.latest_balance.end_date,
              end_balance: liability.latest_balance.end_balance,
            },
            notes: liability.notes,
          };

          await createLiability(account.id, liabilityData);

          // Mark as no longer new
          setValue(`${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}._isNew`, false);
        }
      } else {
        // Update existing liability
        const updates = {
          category: liability.category,
          subcategory: liability.subcategory,
          _displayName: liability._displayName,
          account_holder: liability.account_holder,
          latest_balance: {
            _source: liability.latest_balance._source,
            end_date: liability.latest_balance.end_date,
            end_balance: liability.latest_balance.end_balance,
          },
          notes: liability.notes,
        };

        await updateLiability(account.id, liability.id, updates);
      }
    } catch (error) {
      console.error('Failed to save liability:', error);
      setSaveErrors(prev => new Map(prev).set(recordKey, 'Save failed'));
    } finally {
      setSavingRecords(prev => {
        const next = new Set(prev);
        next.delete(recordKey);
        return next;
      });
    }
  }, [trigger, getValues, setValue, createLiability, updateLiability, saveAccountRecord]);

  // Debounced save handler
  const debouncedSave = useCallback((saveFunction, recordKey, delay = 1000) => {
    // Clear existing timeout for this record
    if (saveTimeouts.current.has(recordKey)) {
      clearTimeout(saveTimeouts.current.get(recordKey));
    }

    // Set new timeout
    const timeoutId = setTimeout(() => {
      saveFunction();
      saveTimeouts.current.delete(recordKey);
    }, delay);

    saveTimeouts.current.set(recordKey, timeoutId);
  }, []);

  // Watch for field changes and trigger saves
  const handleFieldChange = useCallback((account_type, categoryName, accountIndex, liabilityIndex = null) => {
    const recordKey = getRecordKey(account_type, categoryName, accountIndex, liabilityIndex);

    const saveFunction = liabilityIndex !== null
      ? () => saveLiabilityRecord(account_type, categoryName, accountIndex, liabilityIndex)
      : () => saveAccountRecord(account_type, categoryName, accountIndex);

    debouncedSave(saveFunction, recordKey);
  }, [debouncedSave, saveAccountRecord, saveLiabilityRecord]);

  // Settlement adjustment handlers (placeholder)
  const saveSettlementAdjustment = useCallback(async () => {
    const recordKey = 'settlement-adjustment';

    // Clear any existing error
    setSaveErrors(prev => {
      const next = new Map(prev);
      next.delete(recordKey);
      return next;
    });

    setSavingRecords(prev => new Set(prev).add(recordKey));

    try {
      const settlementData = getValues('settlement_adjustment');

      // Validate that we have a value
      if (!settlementData || settlementData.petitioner_final_adjustment === undefined) {
        throw new Error('No settlement adjustment data');
      }

      // TODO: Implement actual API call
      // await mutateSettlementAdjustment(settlementData);
      console.log('Saving settlement adjustment:', settlementData);

      // Simulate API call
      //await new Promise(resolve => setTimeout(resolve, 500));
      mutateSettlementAdjustment(settlementData);

    } catch (error) {
      console.error('Failed to save settlement adjustment:', error);
      setSaveErrors(prev => new Map(prev).set(recordKey, 'Save failed'));
    } finally {
      setSavingRecords(prev => {
        const next = new Set(prev);
        next.delete(recordKey);
        return next;
      });
    }
  }, [getValues]);

  const handleSettlementAdjustmentChange = useCallback(() => {
    const recordKey = 'settlement-adjustment';
    debouncedSave(saveSettlementAdjustment, recordKey);
  }, [debouncedSave, saveSettlementAdjustment]);

  const calculateAccountNetValue = (account) => {
    let netValue = account?.latest_balance?.end_balance || 0;
    if (account.associated_liabilities) {
      netValue += account.associated_liabilities.reduce((sum, liab) => sum + (liab?.latest_balance?.end_balance || 0), 0);
    }
    return netValue;
  };

  const calculateSourceValues = (account) => {
    const end_balance = account?.latest_balance?.end_balance || 0;
    const petitionerSource = account.petitioner_owning_structure.percentage || 0;
    const petitionerValue = account.petitioner_owning_structure.value ?? petitionerSource * end_balance;
    const respondentSource = account.respondent_owning_structure.percentage || 0;
    const respondentValue = account.respondent_owning_structure.value ?? respondentSource * end_balance;
    // Marital nets out non-party "other" stated sources (backend 1.6.3 derive);
    // fall back to 1-pet-res for un-derived accounts. "Other" entities are edited
    // in the Source dialog, so the form has no separate Other column.
    const otherSource = account._stated_other_pct || 0;
    const maritalSource = account._stated_marital_pct ?? (1 - petitionerSource - respondentSource - otherSource);
    const petitionerSettlement = account.petitioner_settlement_structure.percentage || 0;
    const maritalValue = end_balance * maritalSource;
    const petitionerSettlementValue = account.petitioner_settlement_structure.value ?? maritalValue * petitionerSettlement;
    const respondentSettlementValue = maritalValue - petitionerSettlementValue;

    return {
      petitionerValue,
      respondentValue,
      maritalValue,
      petitionerSource,
      respondentSource,
      maritalSource,
      petitionerSettlement,
      respondentSettlement: 1 - petitionerSettlement,
      petitionerSettlementValue,
      respondentSettlementValue
    };
  };

  const calculateEquitySourceValues = (account) => {
    const accountSourceValues = calculateSourceValues(account);
    let totalPetitionerValue = accountSourceValues.petitionerValue;
    let totalRespondentValue = accountSourceValues.respondentValue;
    let totalMaritalValue = accountSourceValues.maritalValue;
    let totalPetitionerSettlementValue = accountSourceValues.petitionerSettlementValue;
    let totalRespondentSettlementValue = accountSourceValues.respondentSettlementValue;

    if (account.associated_liabilities) {
      account.associated_liabilities.forEach(liability => {
        const liabilityValue = liability?.latest_balance?.end_balance || 0;
        totalPetitionerValue += liabilityValue * accountSourceValues.petitionerSource;
        totalRespondentValue += liabilityValue * accountSourceValues.respondentSource;
        const incrementalMaritalValue = liabilityValue * accountSourceValues.maritalSource;
        totalMaritalValue += incrementalMaritalValue;
        const incrementalPetitionerSettlement = incrementalMaritalValue * accountSourceValues.petitionerSettlement;
        totalPetitionerSettlementValue += incrementalPetitionerSettlement;
        const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
        totalRespondentSettlementValue += incrementalRespondentSettlement;
      });
    }

    return {
      petitionerValue: totalPetitionerValue,
      respondentValue: totalRespondentValue,
      maritalValue: totalMaritalValue,
      petitionerSettlementValue: totalPetitionerSettlementValue,
      respondentSettlementValue: totalRespondentSettlementValue
    };
  };

  const getCombinedAccounts = (category) => {
    return category.accounts || [];
  };

  const calculateCategoryTotal = (category) => {
    const accounts = getCombinedAccounts(category);
    return accounts.reduce((sum, account) => sum + calculateAccountNetValue(account), 0);
  };

  const calculateCategorySourceTotals = (category) => {
    const accounts = getCombinedAccounts(category);
    let petitionerTotal = 0;
    let respondentTotal = 0;
    let maritalTotal = 0;
    let petitionerSettlementTotal = 0;
    let respondentSettlementTotal = 0;

    accounts.forEach(account => {
      const sourceValues = calculateSourceValues(account);
      petitionerTotal += sourceValues.petitionerValue;
      respondentTotal += sourceValues.respondentValue;
      maritalTotal += sourceValues.maritalValue;
      petitionerSettlementTotal += sourceValues.petitionerSettlementValue;
      respondentSettlementTotal += sourceValues.respondentSettlementValue;

      if (account.associated_liabilities) {
        account.associated_liabilities.forEach(liability => {
          const liabilityValue = liability?.latest_balance?.end_balance || 0;
          petitionerTotal += liabilityValue * sourceValues.petitionerSource;
          respondentTotal += liabilityValue * sourceValues.respondentSource;
          const incrementalMaritalValue = liabilityValue * sourceValues.maritalSource;
          maritalTotal += incrementalMaritalValue;
          const incrementalPetitionerSettlement = incrementalMaritalValue * sourceValues.petitionerSettlement;
          petitionerSettlementTotal += incrementalPetitionerSettlement;
          const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
          respondentSettlementTotal += incrementalRespondentSettlement;
        });
      }
    });

    return { petitionerTotal, respondentTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
  };

  const calculateAccountTypeTotal = (account_type) => {
    return Object.entries(watchedData[account_type] || {}).reduce((sum, [_, category]) => {
      return sum + calculateCategoryTotal(category);
    }, 0);
  };

  const calculateAccountTypeSourceTotals = (account_type) => {
    let petitionerTotal = 0;
    let respondentTotal = 0;
    let maritalTotal = 0;
    let petitionerSettlementTotal = 0;
    let respondentSettlementTotal = 0;

    Object.entries(watchedData[account_type] || {}).forEach(([_, category]) => {
      const sourceTotals = calculateCategorySourceTotals(category);
      petitionerTotal += sourceTotals.petitionerTotal;
      respondentTotal += sourceTotals.respondentTotal;
      maritalTotal += sourceTotals.maritalTotal;
      petitionerSettlementTotal += sourceTotals.petitionerSettlementTotal;
      respondentSettlementTotal += sourceTotals.respondentSettlementTotal;
    });

    return { petitionerTotal, respondentTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
  };

  const getAvailableCategories = (account_type, excludeExisting) => {
    if (!categoryList) return [];
    const typeFilter = account_type === 'assets' ? 'Asset' : 'Liability';
    const existingCategories = Object.keys(watchedData[account_type] || {});

    return categoryList
      .filter(item => item.type === typeFilter)
      .map(item => item.category)
      .filter((category, index, self) =>
        self.indexOf(category) === index && (!excludeExisting || !existingCategories.includes(category))
      ).sort((a, b) => a.localeCompare(b));
  };

  const getAvailableSubcategories = (account_type, categoryName) => {
    if (!categoryList || !categoryName) return [];
    const typeFilter = account_type === 'assets' ? 'Asset' : 'Liability';

    return categoryList
      .filter(item => item.type === typeFilter && item.category === categoryName)
      .map(item => item.subcategory)
      .filter((subcategory, index, self) =>
        subcategory && self.indexOf(subcategory) === index
      ).sort((a, b) => a.localeCompare(b));
  };

  const isReadonlyAccount = (category, account) => {
    return account._editable === false;
  };

  const createNewAccountObject = (account_type, categoryName) => {
    const newAccountId = uuidv4();
    const newBalanceId = uuidv4();

    return {
      id: newAccountId,
      _editable: true,
      _isNew: true,
      subcategory: '',
      _displayName: '',
      account_holder: '',
      latest_balance: {
        id: newBalanceId,
        _source: '',
        end_date: getLocalDateString(),
        end_balance: null,
      },
      notes: '',
      petitioner_owning_structure: { value: 0, percentage: 0 },
      respondent_owning_structure: { value: 0, percentage: 0 },
      petitioner_settlement_structure: { percentage: 0 },
    };
  };

  const handleAddAccount = (account_type, categoryName) => {
    const newAccount = createNewAccountObject(account_type, categoryName);
    const fieldPath = `${account_type}.${categoryName}.accounts`;
    const currentValue = getValues(fieldPath) || [];
    setValue(fieldPath, [...currentValue, newAccount], { shouldDirty: true });
  };

  const handleDeleteAccount = async (account_type, categoryName, accountIndex) => {
    const account = getValues(`${account_type}.${categoryName}.accounts.${accountIndex}`);
    const recordKey = getRecordKey(account_type, categoryName, accountIndex);

    // If it's a server record (not new), delete it from server
    if (!isNewRecord(account)) {
      setSavingRecords(prev => new Set(prev).add(recordKey));
      try {
        await deleteAccount(account.id);
      } catch (error) {
        console.error('Failed to delete account:', error);
        setSaveErrors(prev => new Map(prev).set(recordKey, 'Delete failed'));
        setSavingRecords(prev => {
          const next = new Set(prev);
          next.delete(recordKey);
          return next;
        });
        return;
      }
      setSavingRecords(prev => {
        const next = new Set(prev);
        next.delete(recordKey);
        return next;
      });
    }

    // Remove from form state
    const fieldPath = `${account_type}.${categoryName}.accounts`;
    const currentAccounts = getValues(fieldPath) || [];
    const updatedAccounts = currentAccounts.filter((_, index) => index !== accountIndex);

    if (updatedAccounts.length === 0) {
      const currentAccountType = getValues(account_type) || {};
      const { [categoryName]: removed, ...rest } = currentAccountType;
      setValue(account_type, rest, { shouldDirty: true });
    } else {
      setValue(fieldPath, updatedAccounts, { shouldDirty: true });
    }

    // Clear error and saving states for this account (ID-based key means no conflicts)
    setSaveErrors(prev => {
      const next = new Map(prev);
      next.delete(recordKey);
      return next;
    });

    setSavingRecords(prev => {
      const next = new Set(prev);
      next.delete(recordKey);
      return next;
    });

    // Clear any pending timeout
    if (saveTimeouts.current.has(recordKey)) {
      clearTimeout(saveTimeouts.current.get(recordKey));
      saveTimeouts.current.delete(recordKey);
    }
  };

  const handleAddLiability = (account_type, categoryName, accountIndex) => {
    const newLiabilityId = uuidv4();
    const newBalanceId = uuidv4();

    const newLiability = {
      id: newLiabilityId,
      _editable: true,
      _isNew: true,
      category: defaultAssociatedLiabilityMap ? defaultAssociatedLiabilityMap[categoryName] : '',
      subcategory: '',
      _displayName: '',
      account_holder: '',
      latest_balance: {
        id: newBalanceId,
        _source: '',
        end_date: getLocalDateString(),
        end_balance: null,
      },
      notes: ''
    };

    const fieldPath = `${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities`;
    const currentLiabilities = getValues(fieldPath) || [];
    setValue(fieldPath, [...currentLiabilities, newLiability], { shouldDirty: true });
  };

  const handleDeleteLiability = async (account_type, categoryName, accountIndex, liabilityIndex) => {
    const account = getValues(`${account_type}.${categoryName}.accounts.${accountIndex}`);
    const liability = account.associated_liabilities[liabilityIndex];
    const recordKey = getRecordKey(account_type, categoryName, accountIndex, liabilityIndex);

    // If it's a server record (not new), delete it from server
    if (!isNewRecord(liability)) {
      setSavingRecords(prev => new Set(prev).add(recordKey));
      try {
        await deleteLiability(account.id, liability.id);
      } catch (error) {
        console.error('Failed to delete liability:', error);
        setSaveErrors(prev => new Map(prev).set(recordKey, 'Delete failed'));
        setSavingRecords(prev => {
          const next = new Set(prev);
          next.delete(recordKey);
          return next;
        });
        return;
      }
      setSavingRecords(prev => {
        const next = new Set(prev);
        next.delete(recordKey);
        return next;
      });
    }

    // Remove from form state
    const fieldPath = `${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities`;
    const currentLiabilities = getValues(fieldPath) || [];
    const updatedLiabilities = currentLiabilities.filter((_, index) => index !== liabilityIndex);
    setValue(fieldPath, updatedLiabilities, { shouldDirty: true });

    // Clear error and saving states
    setSaveErrors(prev => {
      const next = new Map(prev);
      next.delete(recordKey);
      return next;
    });

    setSavingRecords(prev => {
      const next = new Set(prev);
      next.delete(recordKey);
      return next;
    });

    // Clear any pending timeout
    if (saveTimeouts.current.has(recordKey)) {
      clearTimeout(saveTimeouts.current.get(recordKey));
      saveTimeouts.current.delete(recordKey);
    }
  };

  const handleAddCategory = () => {
    if (!newCategoryName) return;

    const newAccount = createNewAccountObject(newCategoryType, newCategoryName);
    const newCategory = { accounts: [newAccount] };

    const currentAccountType = getValues(newCategoryType) || {};
    setValue(`${newCategoryType}.${newCategoryName}`, newCategory, { shouldDirty: true });

    setNewCategoryName('');
    setShowAddCategory(false);
  };

  const openNotesDialog = (account_type, categoryName, accountIndex, liabilityIndex, currentNotes) => {
    setNotesDialog({
      account_type,
      categoryName,
      accountIndex,
      liabilityIndex,
      notes: currentNotes || ''
    });
  };

  const openSettlementAdjustmentNotesDialog = (currentNotes) => {
    setNotesDialog({
      isSettlementAdjustment: true,
      notes: currentNotes || ''
    });
  };

  const saveNotes = () => {
    if (!notesDialog) return;

    const { account_type, categoryName, accountIndex, liabilityIndex, notes, isSettlementAdjustment } = notesDialog;

    if (isSettlementAdjustment) {
      // Handle settlement adjustment notes
      setValue('settlement_adjustment.notes', notes, { shouldDirty: true });
      // Trigger save for settlement adjustment
      handleSettlementAdjustmentChange();
    } else if (liabilityIndex !== null && liabilityIndex !== undefined) {
      setValue(
        `${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.notes`,
        notes,
        { shouldDirty: true }
      );
      // Trigger save for liability
      handleFieldChange(account_type, categoryName, accountIndex, liabilityIndex);
    } else {
      setValue(
        `${account_type}.${categoryName}.accounts.${accountIndex}.notes`,
        notes,
        { shouldDirty: true }
      );
      // Trigger save for account
      handleFieldChange(account_type, categoryName, accountIndex);
    }

    setNotesDialog(null);
  };

  // Save status indicator component
  const SaveStatusIndicator = ({ recordKey, record }) => {
    const isSaving = savingRecords.has(recordKey);
    const hasError = saveErrors.has(recordKey);
    const isNew = isNewRecord(record);

    if (isSaving) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CircularProgress size={12} />
          <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
            Saving...
          </Typography>
        </Box>
      );
    }

    if (hasError) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
          <Typography variant="caption" sx={{ fontSize: '10px', color: 'error.main' }}>
            {saveErrors.get(recordKey)}
          </Typography>
        </Box>
      );
    }

    if (isNew) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FiberNewIcon sx={{ fontSize: 14, color: '#1976d2' }} />
          <Typography variant="caption" sx={{ fontSize: '10px', color: '#1976d2' }}>
            New
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" sx={{ fontSize: '10px', color: 'success.main' }}>
          Saved
        </Typography>
      </Box>
    );
  };

  const TotalsTable = ({ title, value, petitionerTotal, respondentTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal }) => (
    <>
      <Typography variant="h4" sx={{ ml: 1 }}>
        {title}
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 2, p: 0 }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '60px', minWidth: '60px', maxWidth: '60px' }}>Status</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '60px', minWidth: '60px', maxWidth: '60px' }}>Actions</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '100px', minWidth: '100px', maxWidth: '100px' }}>Category</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '130px', minWidth: '130px', maxWidth: '130px' }}>Subcategory</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '150px', minWidth: '150px', maxWidth: '150px' }}>Account</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '160px', minWidth: '160px', maxWidth: '160px' }}>Title</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '70px', minWidth: '70px', maxWidth: '70px' }}>Source</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '85px', minWidth: '85px', maxWidth: '85px' }}>Date</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Value</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Pet. Source</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Resp. Source</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Marital</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Pet. Settle</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Resp. Settle</FormTableHeaderCell>
              <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '65px', minWidth: '65px', maxWidth: '65px' }}>Notes</FormTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
              <FormTableTotalCell sx={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}></FormTableTotalCell>
              <FormTableTotalCell sx={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}></FormTableTotalCell>
              <FormTableTotalCell sx={{ width: '100px', minWidth: '100px', maxWidth: '100px' }}></FormTableTotalCell>
              <FormTableTotalCell sx={{ width: '130px', minWidth: '130px', maxWidth: '130px' }}></FormTableTotalCell>
              <FormTableTotalCell sx={{ fontWeight: 'bold', width: '150px', minWidth: '150px', maxWidth: '150px' }}>{title}</FormTableTotalCell>
              <FormTableTotalCell sx={{ width: '160px', minWidth: '160px', maxWidth: '160px' }}></FormTableTotalCell>
              <FormTableTotalCell sx={{ width: '70px', minWidth: '70px', maxWidth: '70px' }}></FormTableTotalCell>
              <FormTableTotalCell sx={{ width: '85px', minWidth: '85px', maxWidth: '85px' }}></FormTableTotalCell>
              <FormTableTotalCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>{formatCurrency(value)}</FormTableTotalCell>
              <FormTableTotalCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>{formatCurrency(petitionerTotal)}</FormTableTotalCell>
              <FormTableTotalCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>{formatCurrency(respondentTotal)}</FormTableTotalCell>
              <FormTableTotalCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>{formatCurrency(maritalTotal)}</FormTableTotalCell>
              <FormTableTotalCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>{formatCurrency(petitionerSettlementTotal)}</FormTableTotalCell>
              <FormTableTotalCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>{formatCurrency(respondentSettlementTotal)}</FormTableTotalCell>
              <FormTableTotalCell sx={{ width: '65px', minWidth: '65px', maxWidth: '65px' }}></FormTableTotalCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const renderCategory = (account_type, categoryName, category) => {
    const formAccounts = watchedData?.[account_type]?.[categoryName]?.accounts || [];

    return (
      <Box key={categoryName} sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mb: 2
          }}
          onClick={() => toggleCategory(account_type, categoryName)}
        >
          {collapsedCategories[`${account_type}-${categoryName}`] ? <ChevronRightIcon /> : <ExpandMoreIcon />}
          <Typography variant="h4" sx={{ ml: 1 }}>
            {categoryName}
          </Typography>
        </Box>
        {!collapsedCategories[`${account_type}-${categoryName}`] && (
          <>
            <TableContainer component={Paper} sx={{ p: 0 }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '60px', minWidth: '60px', maxWidth: '60px' }}>Status</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '60px', minWidth: '60px', maxWidth: '60px' }}>Actions</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '100px', minWidth: '100px', maxWidth: '100px' }}>Category</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '130px', minWidth: '130px', maxWidth: '130px' }}>Subcategory</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '150px', minWidth: '150px', maxWidth: '150px' }}>Account</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '160px', minWidth: '160px', maxWidth: '160px' }}>Title</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '70px', minWidth: '70px', maxWidth: '70px' }}>Source</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '85px', minWidth: '85px', maxWidth: '85px' }}>Date</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Value</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Pet. Source</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Resp. Source</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Marital</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Pet. Settle</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Resp. Settle</FormTableHeaderCell>
                    <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '65px', minWidth: '65px', maxWidth: '65px' }}>Notes</FormTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formAccounts.map((account, accountIndex) => {
                    const isReadonly = isReadonlyAccount(category, account);
                    const sourceValues = calculateSourceValues(account);
                    const recordKey = getRecordKey(account_type, categoryName, accountIndex);

                    return (
                      <React.Fragment key={`${account.id}-${accountIndex}`}>
                        <TableRow sx={{
                          backgroundColor: isReadonly ? '#f9f9f9' : 'white'
                        }}>
                          <FormTableDetailCell sx={{ py: 0.5, px: 0.5 }}>
                            <SaveStatusIndicator recordKey={recordKey} record={account} />
                          </FormTableDetailCell>
                          <FormTableDetailCell sx={{ py: 0.5, px: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {account_type === 'assets' && !isReadonly && (
                                <ActionIconButton
                                  color="success"
                                  onClick={() => handleAddLiability(account_type, categoryName, accountIndex)}
                                  title="Add Liability"
                                  Icon={AddIcon}
                                  disabled={savingRecords.has(recordKey)}
                                />
                              )}
                              {!isReadonly && (
                                <ActionIconButton
                                  color="error"
                                  onClick={() => handleDeleteAccount(account_type, categoryName, accountIndex)}
                                  title="Delete Account"
                                  Icon={DeleteIcon}
                                  disabled={savingRecords.has(recordKey)}
                                />
                              )}
                              {/* Source-of-funds editing is a role-neutral row action — not bound to
                                  the petitioner column — since a case may have no declared parties. */}
                              <ActionIconButton
                                color="primary"
                                onClick={() => setSourceEditAccount(account)}
                                title="Edit source of funds"
                                Icon={PaidIcon}
                                disabled={savingRecords.has(recordKey)}
                              />
                            </Box>
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', fontSize: '12px' }}>
                              {categoryName}
                            </Typography>
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            {!isReadonly ? (
                              <Controller
                                name={`${account_type}.${categoryName}.accounts.${accountIndex}.subcategory`}
                                control={control}
                                defaultValue={account.subcategory ?? null}
                                rules={{
                                  required: {
                                    value: true,
                                    message: "Subcategory is required"
                                  },
                                  validate: value => value?.trim() !== '' || "Subcategory cannot be empty"
                                }}
                                render={({ field, fieldState }) => (
                                  <TrackedAutocomplete
                                    field={field}
                                    disabled={savingRecords.has(recordKey)}
                                    options={getAvailableSubcategories(account_type, categoryName)}
                                    freeSolo
                                    onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex)}
                                    textFieldProps={{
                                      placeholder: "Select subcategory",
                                      variant: "outlined",
                                      size: "small",
                                      error: !!fieldState.error,
                                      helperText: fieldState.error?.message,
                                      sx: { '& .MuiInputBase-input': { fontSize: '12px' } }
                                    }}
                                  />
                                )}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', fontSize: '12px' }}>
                                {account.subcategory}
                              </Typography>
                            )}
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            {!isReadonly ? (
                              <Controller
                                name={`${account_type}.${categoryName}.accounts.${accountIndex}._displayName`}
                                control={control}
                                defaultValue={account._displayName ?? null}
                                rules={{
                                  required: {
                                    value: true,
                                    message: "Account name is required"
                                  },
                                  validate: value => value?.trim() !== '' || "Account name cannot be empty"
                                }}
                                render={({ field, fieldState }) => (
                                  <TrackedTextField
                                    field={field}
                                    disabled={savingRecords.has(recordKey)}
                                    onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex)}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }}
                                  />
                                )}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', fontSize: '12px' }}>
                                {account._displayName}
                              </Typography>
                            )}
                            {/* Suspected (Livi-proposed from transfers) / no statements yet — the
                                practitioner's "still to request" checklist, right on the row. */}
                            {getAccountStatusBadges(account, categoryName).length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {getAccountStatusBadges(account, categoryName).map((badge) => (
                                  <span key={badge.label} title={badge.title}>
                                    <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
                                  </span>
                                ))}
                              </Box>
                            )}
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            <TitleCell
                              account={account}
                              readonly={false}
                              onEdit={() => setTitleEditAccount(account)}
                            />
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            {!isReadonly ? (
                              <Controller
                                name={`${account_type}.${categoryName}.accounts.${accountIndex}.latest_balance._source`}
                                control={control}
                                defaultValue={account?.latest_balance?._source ?? null}
                                render={({ field }) => (
                                  <TrackedTextField
                                    field={field}
                                    disabled={savingRecords.has(recordKey)}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={field.value ?? ''}
                                    onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex)}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }}
                                  />
                                )}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', fontSize: '12px' }}>
                                {account.latest_balance?._source}
                              </Typography>
                            )}
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            {!isReadonly ? (
                              <Controller
                                name={`${account_type}.${categoryName}.accounts.${accountIndex}.latest_balance.end_date`}
                                control={control}
                                defaultValue={account?.latest_balance?.end_date ?? null}
                                render={({ field }) => (
                                  <TrackedTextField
                                    field={field}
                                    disabled={savingRecords.has(recordKey)}
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={field.value ?? ''}
                                    onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex)}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }}
                                  />
                                )}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', fontSize: '12px' }}>
                                {formatDate(account.latest_balance?.end_date)}
                              </Typography>
                            )}
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            {!isReadonly ? (
                              <Controller
                                name={`${account_type}.${categoryName}.accounts.${accountIndex}.latest_balance.end_balance`}
                                control={control}
                                defaultValue={account?.latest_balance?.end_balance ?? null}
                                rules={{
                                  validate: {
                                    correctSign: (value) => {
                                      if (account_type === 'assets') {
                                        return value >= 0 || "Asset values must be positive";
                                      } else if (account_type === 'liabilities') {
                                        return value <= 0 || "Liability values must be negative";
                                      }
                                      return true;
                                    }
                                  }
                                }}
                                render={({ field, fieldState }) => (
                                  <TrackedNumberField
                                    field={field}
                                    disabled={savingRecords.has(recordKey)}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex)}
                                    decimals={2}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{
                                      step: 0.01
                                    }}
                                    sx={{
                                      '& .MuiInputBase-input': {
                                        fontSize: '12px',
                                        textAlign: 'center'
                                      },
                                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                      },
                                      '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                      }
                                    }}
                                  />
                                )}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', fontSize: '12px', textAlign: 'center' }}>
                                {formatCurrency(account.latest_balance?.end_balance)}
                              </Typography>
                            )}
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            <SourceCell account={account} role="petitioner" />
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            <SourceCell account={account} role="respondent" />
                          </FormTableDetailCell>
                          <FormTableDetailCell align="center">
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                              <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                                {formatCurrency(sourceValues.maritalValue)}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#333' }}>
                                {formatPercentage(sourceValues.maritalSource)}
                              </Typography>
                            </Box>
                          </FormTableDetailCell>
                          <FormTableDetailCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                              {/* <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                                {formatCurrency(sourceValues.petitionerSettlementValue)}
                              </Typography> */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                                <Controller
                                  name={`${account_type}.${categoryName}.accounts.${accountIndex}.petitioner_settlement_structure`}
                                  control={control}
                                  defaultValue={account.petitioner_settlement_structure ?? null}
                                  rules={{
                                    validate: {
                                      settlementRange: (value) => {
                                        if (account_type === 'assets') {
                                          return ((value?.value ?? 0) >= 0 && (value?.value ?? 0) <= Math.round(sourceValues.maritalValue || 0, 2)) || "Petitioner settlement must be between 0 and marital value";
                                        } else if (account_type === 'liabilities') {
                                          return ((value?.value ?? 0) <= 0 && (value?.value ?? 0) >= Math.round((sourceValues.maritalValue || 0), 2)) || "Petitioner settlement must be between 0 and marital value";
                                        }
                                      }
                                    }
                                  }}
                                  render={({ field, fieldState }) => (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                      <ValuePercentField
                                        field={field}
                                        basis={sourceValues.maritalValue || 0}
                                        disabled={savingRecords.has(recordKey)}
                                        onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex)}
                                      />
                                      {fieldState.error && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: '9px',
                                            color: '#d32f2f',
                                            marginTop: '2px',
                                            textAlign: 'center',
                                            maxWidth: '120px'
                                          }}
                                        >
                                          {fieldState.error.message}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                />
                              </Box>
                            </Box>
                          </FormTableDetailCell>
                          <FormTableDetailCell align="center">
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                              <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                                {formatCurrency(sourceValues.respondentSettlementValue)}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#333' }}>
                                {formatPercentage(sourceValues.respondentSettlement)}
                              </Typography>
                            </Box>
                          </FormTableDetailCell>
                          <FormTableDetailCell sx={{ maxWidth: '150px', width: '150px' }}>
                            <NotesField
                              notes={account.notes}
                              onEditClick={() => openNotesDialog(account_type, categoryName, accountIndex, null, account.notes)}
                              disabled={savingRecords.has(recordKey)}
                            />
                            {/* <NotesField
                              account={account}
                              account_type={account_type}
                              categoryName={categoryName}
                              accountIndex={accountIndex}
                              liabilityIndex={null}
                              openNotesDialog={openNotesDialog}
                              disabled={savingRecords.has(recordKey)}
                            /> */}
                          </FormTableDetailCell>
                        </TableRow>
                        {account.associated_liabilities?.map((liability, liabilityIndex) => {
                          const liabilityValue = liability.latest_balance?.end_balance || 0;
                          const liabilityPetitionerValue = liabilityValue * sourceValues.petitionerSource;
                          const liabilityRespondentValue = liabilityValue * sourceValues.respondentSource;
                          const liabilityMaritalValue = liabilityValue * sourceValues.maritalSource;
                          const liabilityPetitionerSettlementValue = liabilityMaritalValue * sourceValues.petitionerSettlement;
                          const liabilityRespondentSettlementValue = liabilityMaritalValue - liabilityPetitionerSettlementValue;
                          const liabilityRecordKey = getRecordKey(account_type, categoryName, accountIndex, liabilityIndex);

                          return (
                            <TableRow key={`${liability.id}-${liabilityIndex}`}>
                              <FormTableLiabilityCell>
                                <SaveStatusIndicator recordKey={liabilityRecordKey} record={liability} />
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell>
                                <ActionIconButton
                                  color="error"
                                  onClick={() => handleDeleteLiability(account_type, categoryName, accountIndex, liabilityIndex)}
                                  title="Delete Associated Liability"
                                  Icon={DeleteIcon}
                                  disabled={savingRecords.has(liabilityRecordKey)}
                                />
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell align="left">
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <Typography sx={{ mr: 1, flexShrink: 0 }}>↳</Typography>
                                  <Controller
                                    name={`${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.category`}
                                    control={control}
                                    defaultValue={liability.category ?? null}
                                    rules={{
                                      required: {
                                        value: true,
                                        message: "Category is required"
                                      },
                                      validate: value => value?.trim() !== '' || "Category cannot be empty"
                                    }}
                                    render={({ field, fieldState }) => (
                                      <TrackedAutocomplete
                                        field={field}
                                        disabled={savingRecords.has(liabilityRecordKey)}
                                        options={getAvailableCategories("liabilities", false)}
                                        freeSolo
                                        fullWidth
                                        onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex, liabilityIndex)}
                                        onChangeCallback={(newValue) => {
                                          // Clear subcategory when category changes
                                          setValue(`${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.subcategory`, '');
                                        }}
                                        textFieldProps={{
                                          placeholder: "Select category",
                                          variant: "outlined",
                                          size: "small",
                                          error: !!fieldState.error,
                                          helperText: fieldState.error?.message,
                                          sx: { '& .MuiInputBase-input': { fontSize: '12px' } }
                                        }}
                                      />
                                    )}
                                  />
                                </Box>
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell align="left">
                                <Controller
                                  name={`${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.subcategory`}
                                  control={control}
                                  defaultValue={liability.subcategory ?? null}
                                  rules={{
                                    required: {
                                      value: true,
                                      message: "Subcategory is required"
                                    },
                                    validate: value => value?.trim() !== '' || "Subcategory cannot be empty"
                                  }}
                                  render={({ field, fieldState }) => (
                                    <TrackedAutocomplete
                                      field={field}
                                      disabled={savingRecords.has(liabilityRecordKey)}
                                      options={getAvailableSubcategories("liabilities", liability.category)}
                                      freeSolo
                                      fullWidth
                                      onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex, liabilityIndex)}
                                      textFieldProps={{
                                        placeholder: "Select subcategory",
                                        variant: "outlined",
                                        size: "small",
                                        error: !!fieldState.error,
                                        helperText: fieldState.error?.message,
                                        sx: { '& .MuiInputBase-input': { fontSize: '12px' } }
                                      }}
                                    />
                                  )}
                                />
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell sx={{ fontStyle: 'italic' }}>
                                <Controller
                                  name={`${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}._displayName`}
                                  control={control}
                                  defaultValue={liability._displayName ?? null}
                                  rules={{
                                    required: {
                                      value: true,
                                      message: "Account name is required"
                                    },
                                    validate: value => value?.trim() !== '' || "Account name cannot be empty"
                                  }}
                                  render={({ field, fieldState }) => (
                                    <TrackedTextField
                                      field={field}
                                      disabled={savingRecords.has(liabilityRecordKey)}
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      value={field.value ?? ''}
                                      onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex, liabilityIndex)}
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }}
                                    />
                                  )}
                                />
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell>
                                <TitleCell
                                  account={liability}
                                  readonly={false}
                                  onEdit={() => setTitleEditAccount(liability)}
                                />
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell>
                                <Controller
                                  name={`${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.latest_balance._source`}
                                  control={control}
                                  defaultValue={liability?.latest_balance?._source ?? null}
                                  render={({ field }) => (
                                    <TrackedTextField
                                      field={field}
                                      disabled={savingRecords.has(liabilityRecordKey)}
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      value={field.value ?? ''}
                                      onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex, liabilityIndex)}
                                      sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }}
                                    />
                                  )}
                                />
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell>
                                <Controller
                                  name={`${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.latest_balance.end_date`}
                                  control={control}
                                  defaultValue={liability?.latest_balance?.end_date ?? null}
                                  render={({ field }) => (
                                    <TrackedTextField
                                      field={field}
                                      disabled={savingRecords.has(liabilityRecordKey)}
                                      type="date"
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      value={field.value ?? ''}
                                      onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex, liabilityIndex)}
                                      sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }}
                                    />
                                  )}
                                />
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell>
                                <Controller
                                  name={`${account_type}.${categoryName}.accounts.${accountIndex}.associated_liabilities.${liabilityIndex}.latest_balance.end_balance`}
                                  control={control}
                                  defaultValue={liability?.latest_balance?.end_balance ?? null}
                                  rules={{
                                    validate: {
                                      correctSign: (value) => {
                                        return value <= 0 || "Liability values must be negative";
                                      }
                                    }
                                  }}
                                  render={({ field, fieldState }) => (
                                    <TrackedNumberField
                                      field={field}
                                      disabled={savingRecords.has(liabilityRecordKey)}
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      onBlurHandler={() => handleFieldChange(account_type, categoryName, accountIndex, liabilityIndex)}
                                      decimals={2}
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      inputProps={{
                                        step: 0.01
                                      }}
                                      sx={{
                                        '& .MuiInputBase-input': {
                                          fontSize: '12px',
                                          textAlign: 'center'
                                        },
                                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                          WebkitAppearance: 'none',
                                          margin: 0,
                                        },
                                        '& input[type=number]': {
                                          MozAppearance: 'textfield',
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell align="center">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                  <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                                    {formatCurrency(liabilityPetitionerValue)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                                    {formatPercentage(sourceValues.petitionerSource)}
                                  </Typography>
                                </Box>
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell align="center">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                  <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                                    {formatCurrency(liabilityRespondentValue)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                                    {formatPercentage(sourceValues.respondentSource)}
                                  </Typography>
                                </Box>
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell align="center">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                  <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                                    {formatCurrency(liabilityMaritalValue)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                                    {formatPercentage(sourceValues.maritalSource)}
                                  </Typography>
                                </Box>
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell align="center">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                  <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                                    {formatCurrency(liabilityPetitionerSettlementValue)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                                    {formatPercentage(sourceValues.petitionerSettlement)}
                                  </Typography>
                                </Box>
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell align="center">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                  <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                                    {formatCurrency(liabilityRespondentSettlementValue)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                                    {formatPercentage(sourceValues.respondentSettlement)}
                                  </Typography>
                                </Box>
                              </FormTableLiabilityCell>
                              <FormTableLiabilityCell sx={{ maxWidth: '150px', width: '150px' }}>
                                <NotesField
                                  notes={liability.notes}
                                  onEditClick={() => openNotesDialog(account_type, categoryName, accountIndex, liabilityIndex, liability.notes)}
                                  disabled={savingRecords.has(liabilityRecordKey)}
                                />
                                {/* <NotesField
                                  account={liability}
                                  account_type={account_type}
                                  categoryName={categoryName}
                                  accountIndex={accountIndex}
                                  liabilityIndex={liabilityIndex}
                                  openNotesDialog={openNotesDialog}
                                  disabled={savingRecords.has(liabilityRecordKey)}
                                /> */}
                              </FormTableLiabilityCell>
                            </TableRow>
                          );
                        })}
                        {account_type === 'assets' && account.associated_liabilities && account.associated_liabilities.length > 0 && (
                          <TableRow sx={{ backgroundColor: '#f0f8ff', fontWeight: 'bold' }}>
                            <FormTableEquityCell></FormTableEquityCell>
                            <FormTableEquityCell></FormTableEquityCell>
                            <FormTableEquityCell></FormTableEquityCell>
                            <FormTableEquityCell></FormTableEquityCell>
                            <FormTableEquityCell sx={{ pl: 4 }}>
                              {account._displayName} Net Equity
                            </FormTableEquityCell>
                            <FormTableEquityCell></FormTableEquityCell>
                            <FormTableEquityCell></FormTableEquityCell>
                            <FormTableEquityCell></FormTableEquityCell>
                            <FormTableEquityCell align="center" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(calculateAccountNetValue(account))}
                            </FormTableEquityCell>
                            <FormTableEquityCell align="center" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(calculateEquitySourceValues(account).petitionerValue)}
                            </FormTableEquityCell>
                            <FormTableEquityCell align="center" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(calculateEquitySourceValues(account).respondentValue)}
                            </FormTableEquityCell>
                            <FormTableEquityCell align="center" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(calculateEquitySourceValues(account).maritalValue)}
                            </FormTableEquityCell>
                            <FormTableEquityCell align="center" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(calculateEquitySourceValues(account).petitionerSettlementValue)}
                            </FormTableEquityCell>
                            <FormTableEquityCell align="center" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(calculateEquitySourceValues(account).respondentSettlementValue)}
                            </FormTableEquityCell>
                            <FormTableEquityCell></FormTableEquityCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <TableRow sx={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell sx={{ fontWeight: 'bold' }}>
                      Total {categoryName}
                    </FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(formAccounts.reduce((sum, account) => sum + calculateAccountNetValue(account), 0))}
                    </FormTableTotalCell>
                    {(() => {
                      const sourceTotals = calculateCategorySourceTotals(category);
                      return (
                        <>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(sourceTotals.petitionerTotal)}
                          </FormTableTotalCell>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(sourceTotals.respondentTotal)}
                          </FormTableTotalCell>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(sourceTotals.maritalTotal)}
                          </FormTableTotalCell>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(sourceTotals.petitionerSettlementTotal)}
                          </FormTableTotalCell>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(sourceTotals.respondentSettlementTotal)}
                          </FormTableTotalCell>
                        </>
                      );
                    })()}
                    <FormTableTotalCell></FormTableTotalCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => handleAddAccount(account_type, categoryName)}
              sx={{ mt: 1 }}
            >
              Add Account
            </Button>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: 1,
        fontFamily: 'var(--font-body)',
        maxWidth: '100%',
        margin: '0 auto',
        position: 'relative'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
          Manage MBS Accounts
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {savingRecords.size > 0 && (
            <Chip
              label={`${savingRecords.size} record${savingRecords.size > 1 ? 's' : ''} saving...`}
              color="warning"
              size="small"
            />
          )}
          {saveErrors.size > 0 && (
            <Chip
              label={`${saveErrors.size} error${saveErrors.size > 1 ? 's' : ''}`}
              color="error"
              size="small"
            />
          )}
          {savingRecords.size === 0 && saveErrors.size === 0 && !isAccountLoading && (
            <Chip label="All changes auto-saved" color="success" size="small" />
          )}
          {accountError && (
            <Chip label="Failed to load data" color="error" size="small" />
          )}
        </Box>
      </Box>

      {isAccountLoading ? (
        <>
          <Box sx={{ mb: 4 }}>
            <Skeleton variant="text" width={150} height={50} sx={{ mb: 2 }} />
            <AccountTableSkeleton />
            <AccountTableSkeleton />
          </Box>
          <Box sx={{ borderTop: '2px solid #ddd', my: 4 }}></Box>
          <Box>
            <Skeleton variant="text" width={180} height={50} sx={{ mb: 2 }} />
            <AccountTableSkeleton />
          </Box>
        </>
      ) : (
        <>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => toggleSection('assets')}>
                {collapsedSections.assets ? <ChevronRightIcon /> : <ExpandMoreIcon />}
                <Typography variant="h4" sx={{ ml: 1, fontWeight: 'bold' }}>Assets</Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddCategory(true)}
              >
                Add New Category
              </Button>
            </Box>
            {!collapsedSections.assets && Object.entries(watchedData.assets || {}).map(([categoryName, category]) =>
              renderCategory('assets', categoryName, category)
            )}

            {(() => {
              const assetSourceTotals = calculateAccountTypeSourceTotals('assets');
              return (
                <TotalsTable
                  title="Total Assets"
                  value={calculateAccountTypeTotal('assets')}
                  petitionerTotal={assetSourceTotals.petitionerTotal}
                  respondentTotal={assetSourceTotals.respondentTotal}
                  maritalTotal={assetSourceTotals.maritalTotal}
                  petitionerSettlementTotal={assetSourceTotals.petitionerSettlementTotal}
                  respondentSettlementTotal={assetSourceTotals.respondentSettlementTotal}
                />
              );
            })()}
          </Box>

          <Box sx={{ borderTop: '2px solid #ddd', my: 4 }}></Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 3 }} onClick={() => toggleSection('liabilities')}>
              {collapsedSections.liabilities ? <ChevronRightIcon /> : <ExpandMoreIcon />}
              <Typography variant="h4" sx={{ ml: 1, fontWeight: 'bold' }}>Liabilities</Typography>
            </Box>
            {!collapsedSections.liabilities && Object.entries(watchedData.liabilities || {}).map(([categoryName, category]) =>
              renderCategory('liabilities', categoryName, category)
            )}

            {(() => {
              const liabilitySourceTotals = calculateAccountTypeSourceTotals('liabilities');
              return (
                <TotalsTable
                  title="Total Liabilities"
                  value={calculateAccountTypeTotal('liabilities')}
                  petitionerTotal={liabilitySourceTotals.petitionerTotal}
                  respondentTotal={liabilitySourceTotals.respondentTotal}
                  maritalTotal={liabilitySourceTotals.maritalTotal}
                  petitionerSettlementTotal={liabilitySourceTotals.petitionerSettlementTotal}
                  respondentSettlementTotal={liabilitySourceTotals.respondentSettlementTotal}
                />
              );
            })()}
          </Box>

          <Box sx={{ borderTop: '2px solid #ddd', my: 4 }}></Box>

          {(() => {
            const assetSourceTotals = calculateAccountTypeSourceTotals('assets');
            const liabilitySourceTotals = calculateAccountTypeSourceTotals('liabilities');
            const netWorth = calculateAccountTypeTotal('assets') + calculateAccountTypeTotal('liabilities');
            const netPetitioner = assetSourceTotals.petitionerTotal + liabilitySourceTotals.petitionerTotal;
            const netRespondent = assetSourceTotals.respondentTotal + liabilitySourceTotals.respondentTotal;
            const netMarital = assetSourceTotals.maritalTotal + liabilitySourceTotals.maritalTotal;
            const netPetitionerSettlement = assetSourceTotals.petitionerSettlementTotal + liabilitySourceTotals.petitionerSettlementTotal;
            const netRespondentSettlement = assetSourceTotals.respondentSettlementTotal + liabilitySourceTotals.respondentSettlementTotal;
            return (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 3 }} >
                  <Typography variant="h4" sx={{ ml: 1, fontWeight: 'bold' }}>Net Worth</Typography>
                </Box>
                <TotalsTable
                  title="Total Net Worth"
                  value={netWorth}
                  petitionerTotal={netPetitioner}
                  respondentTotal={netRespondent}
                  maritalTotal={netMarital}
                  petitionerSettlementTotal={netPetitionerSettlement}
                  respondentSettlementTotal={netRespondentSettlement}
                />
              </>
            );
          })()}

          {/* Settlement Adjustment Section */}
          {!isAccountLoading && (
            <>
              <Box sx={{ borderTop: '2px solid #ddd', my: 4 }}></Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ ml: 1, fontWeight: 'bold' }}>
                  Final Settlement Adjustment
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ mt: 2, p: 0 }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '60px', minWidth: '60px', maxWidth: '60px' }}>Status</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '60px', minWidth: '60px', maxWidth: '60px' }}>Actions</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '100px', minWidth: '100px', maxWidth: '100px' }}>Category</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '130px', minWidth: '130px', maxWidth: '130px' }}>Subcategory</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '150px', minWidth: '150px', maxWidth: '150px' }}>Account</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '160px', minWidth: '160px', maxWidth: '160px' }}>Title</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '70px', minWidth: '70px', maxWidth: '70px' }}>Source</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '85px', minWidth: '85px', maxWidth: '85px' }}>Date</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Value</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Pet. Source</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Resp. Source</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Marital</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Pet. Settle</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px', maxWidth: '80px' }}>Resp. Settle</FormTableHeaderCell>
                      <FormTableHeaderCell align="center" sx={{ fontWeight: 'bold', width: '65px', minWidth: '65px', maxWidth: '65px' }}>Notes</FormTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                      {/* Status Column */}
                      <FormTableDetailCell sx={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>
                        <SaveStatusIndicator
                          recordKey="settlement-adjustment"
                          record={watchedData?.settlement_adjustment || {}}
                        />
                      </FormTableDetailCell>

                      {/* Empty columns to align with settlement columns */}
                      <FormTableDetailCell sx={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}></FormTableDetailCell>
                      <FormTableDetailCell sx={{ width: '100px', minWidth: '100px', maxWidth: '100px' }}></FormTableDetailCell>
                      <FormTableDetailCell sx={{ width: '130px', minWidth: '130px', maxWidth: '130px' }}></FormTableDetailCell>
                      <FormTableDetailCell sx={{ fontWeight: 'bold', width: '150px', minWidth: '150px', maxWidth: '150px' }}>
                        <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
                          Final Settlement Adjustment
                        </Typography>
                      </FormTableDetailCell>
                      <FormTableDetailCell sx={{ width: '160px', minWidth: '160px', maxWidth: '160px' }}></FormTableDetailCell>
                      <FormTableDetailCell sx={{ width: '70px', minWidth: '70px', maxWidth: '70px' }}></FormTableDetailCell>
                      <FormTableDetailCell sx={{ width: '85px', minWidth: '85px', maxWidth: '85px' }}></FormTableDetailCell>
                      <FormTableDetailCell sx={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}></FormTableDetailCell>
                      <FormTableDetailCell sx={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}></FormTableDetailCell>
                      <FormTableDetailCell sx={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}></FormTableDetailCell>
                      <FormTableDetailCell sx={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}></FormTableDetailCell>

                      {/* Petitioner Settlement Adjustment Column */}
                      <FormTableDetailCell align="center" sx={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                        <Controller
                          name="settlement_adjustment.petitioner_final_adjustment"
                          control={control}
                          defaultValue={initialData?.settlement_adjustment?.petitioner_final_adjustment || 0}
                          render={({ field }) => (
                            <TrackedNumberField
                              field={field}
                              disabled={savingRecords.has('settlement-adjustment')}
                              variant="outlined"
                              size="small"
                              fullWidth
                              value={field.value ?? 0}
                              onBlurHandler={handleSettlementAdjustmentChange}
                              inputProps={{
                                style: { textAlign: 'center' }
                              }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  fontSize: '12px',
                                  textAlign: 'center'
                                }
                              }}
                            />
                          )}
                        />
                      </FormTableDetailCell>

                      {/* Respondent Settlement Adjustment Column (Read-only, negative of petitioner) */}
                      <FormTableDetailCell align="center" sx={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', fontSize: '12px', textAlign: 'center' }}>
                          {formatCurrency(-(watchedData?.settlement_adjustment?.petitioner_final_adjustment || 0))}
                        </Typography>
                      </FormTableDetailCell>

                      {/* Notes Column */}
                      <FormTableDetailCell sx={{ width: '65px', minWidth: '65px', maxWidth: '65px' }}>
                        <NotesField
                          notes={watchedData?.settlement_adjustment?.notes}
                          onEditClick={() => openSettlementAdjustmentNotesDialog(watchedData?.settlement_adjustment?.notes)}
                          disabled={savingRecords.has('settlement-adjustment')}
                        />
                        {/* <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => openSettlementAdjustmentNotesDialog(watchedData?.settlement_adjustment?.notes)}
                            disabled={savingRecords.has('settlement-adjustment')}
                            sx={{
                              padding: '4px',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '11px',
                                color: (watchedData?.settlement_adjustment?.notes ? '#1976d2' : '#999'),
                                fontWeight: (watchedData?.settlement_adjustment?.notes ? 'bold' : 'normal')
                              }}
                            >
                              {watchedData?.settlement_adjustment?.notes ? '' : ''}
                            </Typography>
                          </IconButton>
                        </Box> */}
                      </FormTableDetailCell>
                    </TableRow>
                    <TableRow sx={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell sx={{ fontWeight: 'bold' }}>
                          Final Settlement Total
                    </FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>
                    <FormTableTotalCell></FormTableTotalCell>

                    {(() => {
                      //const sourceTotals = calculateCategorySourceTotals(category);
                      const assetSourceTotals = calculateAccountTypeSourceTotals('assets');
                const liabilitySourceTotals = calculateAccountTypeSourceTotals('liabilities');
                const netWorth = calculateAccountTypeTotal('assets') + calculateAccountTypeTotal('liabilities');
                const netPetitioner = assetSourceTotals.petitionerTotal + liabilitySourceTotals.petitionerTotal;
                const netRespondent = assetSourceTotals.respondentTotal + liabilitySourceTotals.respondentTotal;
                const netMarital = assetSourceTotals.maritalTotal + liabilitySourceTotals.maritalTotal;
                const petitionerFinalAdjustment = watchedData?.settlement_adjustment?.petitioner_final_adjustment ?? 0;
                const netPetitionerSettlement = assetSourceTotals.petitionerSettlementTotal + liabilitySourceTotals.petitionerSettlementTotal
                  + petitionerFinalAdjustment;
                const netRespondentSettlement = assetSourceTotals.respondentSettlementTotal + liabilitySourceTotals.respondentSettlementTotal
                  - petitionerFinalAdjustment;
                      return (
                        <>
                        <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(netWorth)}
                    </FormTableTotalCell>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(netPetitioner)}
                          </FormTableTotalCell>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(netRespondent)}
                          </FormTableTotalCell>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(netMarital)}
                          </FormTableTotalCell>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(netPetitionerSettlement)}
                          </FormTableTotalCell>
                          <FormTableTotalCell align="center" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(netRespondentSettlement)}
                          </FormTableTotalCell>
                        </>
                      );
                    })()}
                    <FormTableTotalCell></FormTableTotalCell>
                  </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      <Dialog
        open={!!notesDialog}
        onClose={() => setNotesDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Notes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={6}
            fullWidth
            variant="outlined"
            value={notesDialog?.notes || ''}
            onChange={(e) => setNotesDialog({ ...notesDialog, notes: e.target.value })}
            placeholder="Enter notes here..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialog(null)}>Cancel</Button>
          <Button onClick={saveNotes} variant="contained">Save Notes</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAddCategory}
        onClose={() => setShowAddCategory(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Account Type</InputLabel>
            <Select
              value={newCategoryType}
              onChange={(e) => {
                setNewCategoryType(e.target.value);
                setNewCategoryName('');
              }}
              label="Account Type"
            >
              <MenuItem value="assets">Asset</MenuItem>
              <MenuItem value="liabilities">Liability</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            options={getAvailableCategories(newCategoryType, true)}
            value={newCategoryName}
            onChange={(event, newValue) => setNewCategoryName(newValue || '')}
            onInputChange={(event, newInputValue) => setNewCategoryName(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category Name"
                placeholder="Select or enter category name"
                variant="outlined"
                fullWidth
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddCategory(false);
              setNewCategoryName('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAddCategory} variant="contained">
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      <AccountTitleEditDialog
        open={!!titleEditAccount}
        account={titleEditAccount}
        onClose={() => setTitleEditAccount(null)}
      />
      <AccountSourceEditDialog
        open={!!sourceEditAccount}
        account={sourceEditAccount}
        onClose={() => setSourceEditAccount(null)}
      />
    </Box>
  );
};

export default AccountManagementForm;